"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useTheme } from "@/components/layout/ThemeProvider";
import { ALL_CAPABILITIES, getCapabilitiesForTier, type Capability } from "@/lib/platform/capabilities";
import type { SubscriptionPlan } from "@/lib/subscription/plans";
import { Check, Lock, Sparkles, Zap, Crown, Info, ExternalLink } from "lucide-react";
import ProductIcon from "@/components/ui/ProductIcon";

export default function CapabilityVaultPage() {
  const router = useRouter();
  const { profile, loading: themeLoading } = useTheme();
  const [ownedCapabilities, setOwnedCapabilities] = useState<Set<string>>(new Set());
  // Use capability tier type (not SubscriptionPlan) to match getCapabilitiesForTier
  const [userTier, setUserTier] = useState<"free" | "personal" | "pro" | "business">("free");

  useEffect(() => {
    // Load owned capabilities from database/localStorage
    // In production, this would come from the database
    const stored = localStorage.getItem("owned_capabilities");
    if (stored) {
      try {
        setOwnedCapabilities(new Set(JSON.parse(stored)));
      } catch (e) {
        console.error("Failed to load owned capabilities:", e);
      }
    }

    // Get user's subscription tier and map to capability tier system
    const plan: SubscriptionPlan = (profile && 'plan' in profile && typeof profile.plan === 'string') 
      ? (profile.plan as SubscriptionPlan)
      : "free";
    
    // Map SubscriptionPlan to capability tier system
    // SubscriptionPlan: "free" | "pro" | "pro_plus" | "enterprise"
    // Capability tier: "free" | "personal" | "pro" | "business"
    const tierMap: Record<SubscriptionPlan, "free" | "personal" | "pro" | "business"> = {
      free: "free",
      pro: "pro",
      pro_plus: "business", // pro_plus maps to business tier
      enterprise: "business", // enterprise maps to business tier
    };
    
    const tier = tierMap[plan];
    setUserTier(tier);
  }, [profile]);

  const availableCapabilities = getCapabilitiesForTier(userTier);
  const owned = availableCapabilities.filter((c) => ownedCapabilities.has(c.id));
  const locked = availableCapabilities.filter((c) => !ownedCapabilities.has(c.id));

  const primaryColor = profile?.brand?.primaryColor || "#6366f1";
  const secondaryColor = profile?.brand?.secondaryColor || "#8b5cf6";
  const textColor = profile?.brand?.textColor || "#000000";
  const textSecondaryColor = profile?.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.7)";

  const getPricingBandIcon = (band: string) => {
    switch (band) {
      case "micro":
        return <Sparkles className="w-4 h-4" />;
      case "power":
        return <Zap className="w-4 h-4" />;
      case "authority":
        return <Crown className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getPricingBandColor = (band: string) => {
    switch (band) {
      case "micro":
        return "#10b981"; // green
      case "power":
        return "#f59e0b"; // amber
      case "authority":
        return "#8b5cf6"; // purple
      default:
        return "#6b7280";
    }
  };

  const getRiskLevelColor = (risk: string) => {
    switch (risk) {
      case "low":
        return "#10b981";
      case "medium":
        return "#f59e0b";
      case "high":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  if (themeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-600 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-4 md:p-8 relative overflow-hidden pb-24"
      style={{
        background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}15 50%, ${primaryColor}10 100%)`,
      }}
    >
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"
          style={{ backgroundColor: primaryColor }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"
          style={{ backgroundColor: secondaryColor }}
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: textColor }}
          >
            Capability Vault
          </h1>
          <div
            className="p-6 rounded-2xl premium-white-glass backdrop-blur-xl border mb-6"
            style={{
              borderColor: `${primaryColor}40`,
              boxShadow: `0 8px 32px ${primaryColor}20, inset 0 1px 0 rgba(255,255,255,0.6)`,
            }}
          >
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: primaryColor }} />
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: textColor }}>
                  Plans vs Purchases
                </h3>
                <p className="text-sm mb-3" style={{ color: textSecondaryColor }}>
                  <strong>Plans decide how smart and autonomous your AI is.</strong>
                  <br />
                  <strong>Purchases decide what your AI is allowed to touch.</strong>
                </p>
                <div className="text-xs space-y-1" style={{ color: textSecondaryColor }}>
                  <p>• <strong>Free:</strong> Observer AI (no actions)</p>
                  <p>• <strong>Personal:</strong> Thinking AI (Tier 0-1 actions)</p>
                  <p>• <strong>Pro:</strong> Acting AI (Tier 2 actions)</p>
                  <p>• <strong>Business:</strong> Orchestrating AI teams (Tier 3 actions)</p>
                </div>
                <p className="text-xs mt-3 italic" style={{ color: textSecondaryColor }}>
                  Once you purchase a capability, it's permanently unlocked for your account and usable by any AI you create.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Owned Capabilities */}
        {owned.length > 0 && (
          <div className="mb-12">
            <h2
              className="text-2xl font-semibold mb-4 flex items-center gap-2"
              style={{ color: textColor }}
            >
              <Check className="w-6 h-6 text-green-600" />
              Owned Capabilities ({owned.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {owned.map((capability) => (
                <div
                  key={capability.id}
                  className="p-6 rounded-2xl premium-white-glass backdrop-blur-xl border transition-all duration-200 hover:scale-[1.02]"
                  style={{
                    borderColor: `${primaryColor}60`,
                    boxShadow: `0 8px 32px ${primaryColor}30, inset 0 1px 0 rgba(255,255,255,0.6)`,
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{
                          background: `linear-gradient(135deg, ${primaryColor}20 0%, ${secondaryColor}20 100%)`,
                        }}
                      >
                        <ProductIcon icon={capability.icon} size={32} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg" style={{ color: textColor }}>
                          {capability.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{
                              backgroundColor: `${getPricingBandColor(capability.pricingBand)}20`,
                              color: getPricingBandColor(capability.pricingBand),
                            }}
                          >
                            {getPricingBandIcon(capability.pricingBand)}
                            <span className="ml-1 capitalize">{capability.pricingBand}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  </div>
                  <p className="text-sm mb-4" style={{ color: textSecondaryColor }}>
                    {capability.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs" style={{ color: textSecondaryColor }}>
                    <span
                      className="px-2 py-0.5 rounded"
                      style={{
                        backgroundColor: `${getRiskLevelColor(capability.riskLevel)}20`,
                        color: getRiskLevelColor(capability.riskLevel),
                      }}
                    >
                      {capability.riskLevel} risk
                    </span>
                    <span>•</span>
                    <span>{capability.actionTypes.join(", ")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Locked Capabilities */}
        <div>
          <h2
            className="text-2xl font-semibold mb-4 flex items-center gap-2"
            style={{ color: textColor }}
          >
            <Lock className="w-6 h-6" />
            Available Capabilities ({locked.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locked.map((capability) => {
              const canPurchase = !capability.partnerOnly && capability.enabled;
              // Check if user's tier is too low for this capability using tier order
              const tierOrder: Record<"free" | "personal" | "pro" | "business", number> = {
                free: 0,
                personal: 1,
                pro: 2,
                business: 3,
              };
              const userTierLevel = tierOrder[userTier];
              const tierBlocked = capability.requiredSubscriptionTier
                ? userTierLevel < tierOrder[capability.requiredSubscriptionTier]
                : false;

              return (
                <div
                  key={capability.id}
                  className="p-6 rounded-2xl premium-white-glass backdrop-blur-xl border transition-all duration-200 hover:scale-[1.02]"
                  style={{
                    borderColor: tierBlocked ? "rgba(0, 0, 0, 0.1)" : `${primaryColor}40`,
                    boxShadow: tierBlocked
                      ? `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`
                      : `0 4px 16px ${primaryColor}20, inset 0 1px 0 rgba(255,255,255,0.6)`,
                    opacity: tierBlocked ? 0.6 : 1,
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                        style={{
                          background: `linear-gradient(135deg, ${primaryColor}20 0%, ${secondaryColor}20 100%)`,
                        }}
                      >
                        <ProductIcon icon={capability.icon} size={32} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg" style={{ color: textColor }}>
                          {capability.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{
                              backgroundColor: `${getPricingBandColor(capability.pricingBand)}20`,
                              color: getPricingBandColor(capability.pricingBand),
                            }}
                          >
                            {getPricingBandIcon(capability.pricingBand)}
                            <span className="ml-1 capitalize">{capability.pricingBand}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    {tierBlocked && (
                      <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm mb-4" style={{ color: textSecondaryColor }}>
                    {capability.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs mb-4" style={{ color: textSecondaryColor }}>
                    <span
                      className="px-2 py-0.5 rounded"
                      style={{
                        backgroundColor: `${getRiskLevelColor(capability.riskLevel)}20`,
                        color: getRiskLevelColor(capability.riskLevel),
                      }}
                    >
                      {capability.riskLevel} risk
                    </span>
                    <span>•</span>
                    <span>{capability.actionTypes.join(", ")}</span>
                  </div>
                  {tierBlocked && (
                    <div className="mb-4 p-2 rounded bg-amber-50 dark:bg-amber-900/20">
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        Requires {capability.requiredSubscriptionTier} plan
                      </p>
                    </div>
                  )}
                  {capability.partnerOnly && (
                    <div className="mb-4 p-2 rounded bg-blue-50 dark:bg-blue-900/20">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        Partner/Enterprise access required
                      </p>
                    </div>
                  )}
                  {canPurchase && !tierBlocked && (
                    <button
                      onClick={() => {
                        // Navigate to purchase flow
                        router.push(`/marketplace?capability=${capability.id}`);
                      }}
                      className="w-full px-4 py-2.5 rounded-xl text-white font-medium transition-all duration-200 hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        boxShadow: `0 4px 16px ${primaryColor}40`,
                      }}
                    >
                      Unlock for ${capability.unlockPriceUSD.toFixed(2)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

